import { useEffect, useSyncExternalStore } from 'react'
import { useAuth } from '@/context/AuthContext'
import { loadCrmAccount, type CrmAccount } from './accountService'
export type BusinessOptions = { useGridReferences: boolean; useDeliveryDay: boolean; useCallDay: boolean }
export const defaultBusinessOptions: BusinessOptions = { useGridReferences: true, useDeliveryDay: true, useCallDay: true }
type Snapshot = { account: CrmAccount | null; loading: boolean; error: string | null }
const entries = new Map<string, ReturnType<typeof createEntry>>()
function createEntry() {
  let snapshot: Snapshot = { account: null, loading: true, error: null }, pending: Promise<void> | null = null
  const listeners = new Set<() => void>()
  const update = (next: Snapshot) => { snapshot = next; listeners.forEach(listener => listener()) }
  const refresh = () => {
    if (pending) return pending
    pending = loadCrmAccount().then(account => update({ account, loading: false, error: null }))
      .catch(error => update({ ...snapshot, loading: false, error: String(error.message ?? error) })).finally(() => { pending = null })
    return pending
  }
  return { snapshot: () => snapshot, subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener) } }, refresh }
}
export function businessOptionsChanged() { window.dispatchEvent(new Event('crm-business-options-changed')) }
export function useBusinessOptions() {
  const { session, isDemo } = useAuth(); const key = `${session?.userId}:${session?.businessId}:${session?.role}`
  if (!entries.has(key)) entries.set(key, createEntry())
  const entry = entries.get(key)!, state = useSyncExternalStore(entry.subscribe, entry.snapshot, entry.snapshot)
  useEffect(() => {
    if (!session?.businessId || isDemo) return
    void entry.refresh()
    const refresh = () => { void entry.refresh() }
    window.addEventListener('crm-business-options-changed', refresh); window.addEventListener('focus', refresh)
    return () => { window.removeEventListener('crm-business-options-changed', refresh); window.removeEventListener('focus', refresh) }
  }, [entry, session?.businessId, isDemo])
  return { ...state, loading: !!session?.businessId && !isDemo && state.loading, refresh: entry.refresh,
    options: { useGridReferences: state.account?.useGridReferences ?? true, useDeliveryDay: state.account?.useDeliveryDay ?? true, useCallDay: state.account?.useCallDay ?? true } }
}
